import { Modal, Card } from "react-bootstrap";
import { Link } from "react-router-dom";
import { ChangeNameReqApi, IntegrationStatus, SubscriptionStatus } from "./Data/ApiTransport";
import {
  SubscriptionParker,
  SubscriptionParkerChangeReq,
  Vehicle,
} from "./Data/ApiTypes";
import { useEffect, useMemo, useState } from "react";
import { Api } from "./Data/Api";
import { processResult } from "./Data/Result";
import { Hash, InfoCircleFill, Person } from "react-bootstrap-icons";
import { Button } from "./Controls/Button";
import { Input } from "./Controls/Input";
import { useForm } from "react-hook-form";
import { emitSubscriptionParker } from "./Data/ApiEmit";
import { toast } from "react-toastify";
import { AddVehicleModal } from "./Controls/AddVehicleModal";
import { ChooseVehicleModal } from "./Controls/ChooseVehicleModal";
import { ReservationVehicleCard } from "./ReservationVehicleCard";
import QrCodeModal from './Controls/QRCodeModal';
import { createApiService } from "./shared/apiConfig";
import { DataEntitiesFacilityIntegrationParty, FacilityApi, ParkerIntegrationPartyApi, ServerRequestsParkerIntegrationPartyDto } from "./shared/api/axios-client";
import { ConfirmModal } from "./Controls/ConfirmModal";
import { ParkerIntegrations } from "./ParkerIntegrations";
import { isAdminRole } from "./helpers/RoleManager";
import { authAtom } from "./Data/Atoms";
import { useAtom } from "jotai";
import { SUBSCRIPTION_ALLOWED_THIRD_PARTY, VEHICLE_ALLOWED_THIRD_PARTY } from "./FacilityParkerSubscriptionEditPage";

interface Props {
  show: boolean;
  onClose: () => void;
  parker: SubscriptionParker;
  setParker: React.Dispatch<
    React.SetStateAction<SubscriptionParker | undefined>
  >;
  parkers: any[];
  setParkers: React.Dispatch<React.SetStateAction<any[]>>;
  selectedParkerSub: any;
  facilityId: number;
  setMainParkerIntegrations?: React.Dispatch<React.SetStateAction<ServerRequestsParkerIntegrationPartyDto[]>>;
}

const ParkerEditModal = ({
  show,
  onClose,
  parker,
  setParker,
  parkers,
  setParkers,
  selectedParkerSub,
  facilityId,
  setMainParkerIntegrations
}: Props) => {
  const hasMoreVehiclesThanSpots = parker.vehicles.length > parker.quantity;
  const allowedSpots = parker.quantity;

  const initialValues = useMemo(() => emitSubscriptionParker(parker), [parker]);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { isDirty, errors },
  } = useForm<SubscriptionParker>({
    defaultValues: initialValues,
  });

  const values = watch();
  const [showAdd, setShowAdd] = useState(false);
  const [showChoose, setShowChoose] = useState(false);
  const [chooseModalVehicles, setChooseModalVehicles] = useState<Vehicle[]>([]);
  const [chooseModalExclude, setChooseModalExclude] = useState<number[]>([]);
  const [integrationQrCodeData, setIntegrationQrCodeData] = useState<string>('');
  const [showIntegrationQrModal, setShowIntegrationQrModal] = useState(false);
  const [showToggleConfirm, setShowToggleConfirm] = useState(false);
  const [showParkerVehicleRemoveConfirm, setShowParkerVehicleRemoveConfirm] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState<Vehicle | null>(null);
  const [pendingToggle, setPendingToggle] = useState<{
    integrationPartyId: number;
    subscriptionId: number;
    parkerId: number;
    facilityId: number;
    newStatus: IntegrationStatus;
    vehicleId?: number;
  } | null>(null);
  const [parkerIntegrations, setParkerIntegrations] = useState<ServerRequestsParkerIntegrationPartyDto[]>([]);
  const [subscriptionIntegrations, setSubscriptionIntegrations] = useState<
    DataEntitiesFacilityIntegrationParty[]
  >([]);
  const [vehicleIntegrations, setVehicleIntegrations] = useState<
    DataEntitiesFacilityIntegrationParty[]
  >([]);
  const [auth, setAuth] = useAtom(authAtom);

  useEffect(() => {
    reset(emitSubscriptionParker(parker));
  }, [parker, reset]);

  const userIsAdmin = isAdminRole(auth.account?.roles ?? []);

  useEffect(() => {
    if (!userIsAdmin) return;

    async function fetchIntegrations() {
      if (!facilityId) return;

      try {
        const api = createApiService(FacilityApi);
        const response = await api.getFacilityIntegrations({ facilityId });

        const vehiclesIntegrations = response.data.filter(
          (x) =>
            VEHICLE_ALLOWED_THIRD_PARTY.includes(x.integrationParty?.name || "") &&
            x.isActive
        );

        const mergedVehiclesIntegrations: ServerRequestsParkerIntegrationPartyDto[] = [
          ...parkerIntegrations.filter((fi) =>
            VEHICLE_ALLOWED_THIRD_PARTY.includes(fi.integrationName || "")
          ),

          ...parker.vehicles.flatMap((vehicle) =>
            vehiclesIntegrations
              .filter(
                (fi) =>
                  !parkerIntegrations.some(
                    (pi) =>
                      pi.integrationPartyId === fi.integrationPartyId &&
                      pi.vehicleId === vehicle.vehicleId
                  )
              )
              .map((fi) => ({
                integrationPartyId: fi.integrationPartyId,
                integrationName: fi.integrationParty?.name || "Ballparc",
                parkerId: 0,
                vehicleId: vehicle.vehicleId,
                isActive: false,
                metaFlashUID: "",
                metaFlashRFIDNumber: "",
              }))
          ),
        ].filter((x): x is NonNullable<typeof x> => x != null);

        setVehicleIntegrations(mergedVehiclesIntegrations);

        const subscriptionsIntegrations = response.data.filter(
          (x) =>
            SUBSCRIPTION_ALLOWED_THIRD_PARTY.includes(x.integrationParty?.name || "") &&
            !parkerIntegrations.some(
              (i) => i.integrationPartyId === x.integrationPartyId
            ) &&
            x.isActive
        );

        const mergedSubscriptionsIntegrations: ServerRequestsParkerIntegrationPartyDto[] = [
        ...parkerIntegrations.filter((fi) =>
          SUBSCRIPTION_ALLOWED_THIRD_PARTY.includes(fi.integrationName || "")
        ),
        ...subscriptionsIntegrations.map((fi) => ({
          integrationPartyId: fi.integrationPartyId,
          integrationName: fi.integrationParty?.name,
          parkerId: 0,
          isActive: false,
          metaFlashUID: "",
          metaFlashRFIDNumber: "",
        })),
      ].filter((x): x is NonNullable<typeof x> => x != null);

        setSubscriptionIntegrations(mergedSubscriptionsIntegrations);
      } catch (err: any) {
        toast.error("An unexpected error occurred");
      }
    }

    fetchIntegrations();
  }, [facilityId, userIsAdmin, parkerIntegrations, parkers]);

  useEffect(() => {
    if (!show) return;

    const fetchData = async () => {
      try {
        // Fetch Parker Integrations
        const integrationApi = createApiService(ParkerIntegrationPartyApi);
        const integrationResponse = await integrationApi.listParkerIntegrationParties({
          subscriptionId: selectedParkerSub.subscriptionId
        });

        setParkerIntegrations(
          integrationResponse.data.items?.filter(x => x.parkerId === parker.parker.parkerId) ?? []
        );
      } catch (err) {
        console.error("Failed to fetch QR code or integrations:", err);
        toast.error("Failed to load access code or integrations.");
      } finally {
      
      }
    }

    fetchData();
  }, [show]);

  const onSubmit = async (data: SubscriptionParker) => {
    let quantityUpdated = false;
    let nameUpdated = false;

    const quantityChanged = data.quantity !== parker.quantity;
    const nameChanged = data.parker.name !== parker.parker.name;

    if (quantityChanged) {
      const quantityRequest: SubscriptionParkerChangeReq = {
        parkerId: data.parker.parkerId,
        quantity: data.quantity,
      };

      await Api.subParkerChange(
        facilityId,
        selectedParkerSub.subscriptionId,
        quantityRequest
      ).then((resp) =>
        processResult(
          resp,
          () => {
            quantityUpdated = true;
          },
          (err) => toast.error(err)
        )
      );
    }

    if (!data.parker.isManaged && nameChanged) {
      const nameReq: ChangeNameReqApi = {
        name: data.parker.name,
      };

      await Api.changeParkerName(parker.parker.parkerId, nameReq).then(
        (resp: any) =>
          processResult(
            resp,
            () => {
              nameUpdated = true;
            },
            (err) => toast.error(err as string)
          )
      );
    }

    if (quantityUpdated || nameUpdated) {
      const updatedParker: any = {
        ...parker,
        quantity: data.quantity,
        name: data.parker.name,
        parker: {
          ...parker.parker,
          name: data.parker.name,
          emailAddress: data.parker.emailAddress,
        },
      };

      setParker(updatedParker);

      const updatedParkers = parkers.map((p) =>
        p.parker.parkerId === updatedParker.parker.parkerId ? updatedParker : p
      );

      setParkers(updatedParkers);
      reset(updatedParker);
      toast.success("Parker Updated Successfully");
    }
  };

  const handleSubscriptionIntegration = async (
    integrationPartyId: number,
    subscriptionId: number,
    parkerId: number,
    facilityId: number,
    newStatus: IntegrationStatus
  ) => {
    try {
      const parkerIntegrationPartyApi = createApiService(ParkerIntegrationPartyApi);
      let updatedIntegration;

      if (newStatus === IntegrationStatus.Activated) {
        const response = await parkerIntegrationPartyApi.parkerIntegrationPartiesSubscriptionEnrollPatch({
          serverRequestsEnrollSubscriptionIntegrationPartiesRequest: {
            subscriptionId,
            parkerId:
              parkerId === 0 || parkerId == null ? parker.parker.parkerId : parkerId,
            facilityId,
            integrationPartyId
          },
        });
        updatedIntegration = response.data.parkerIntegrationParty;
      } else {
        const response = await parkerIntegrationPartyApi.parkerIntegrationPartiesSubscriptionUnrollPatch({
          serverRequestsUnrollSubscriptionIntegrationPartiesRequest: {
            subscriptionId,
            parkerId:
              parkerId === 0 || parkerId == null ? parker.parker.parkerId : parkerId,
            facilityId,
            integrationPartyId
          },
        });
        updatedIntegration = response.data.parkerIntegrationParty;
      }

      return updatedIntegration;
    } catch (err) {
      toast.error("Failed to update subscription integration status");
      throw err;
    }
  };

  const handleVehicleIntegration = async (
    integrationPartyId: number,
    subscriptionId: number,
    parkerId: number,
    vehicleId: number,
    facilityId: number,
    newStatus: IntegrationStatus
  ) => {
    try {
      const parkerIntegrationPartyApi = createApiService(ParkerIntegrationPartyApi);
      let updatedIntegration;

      if (newStatus === IntegrationStatus.Activated) {
        const response = await parkerIntegrationPartyApi.parkerIntegrationPartiesVehicleEnrollPatch({
          serverRequestsEnrollVehicleIntegrationPartiesRequest: {
            subscriptionId,
            parkerId:
              parkerId === 0 || parkerId == null ? parker.parker.parkerId : parkerId,
            facilityId,
            integrationPartyId,
            vehicleId
          },
        });
        updatedIntegration = response.data.parkerIntegrationParty;
      } else {
        const response = await parkerIntegrationPartyApi.parkerIntegrationPartiesVehicleUnrollPatch({
          serverRequestsUnrollVehicleIntegrationPartiesRequest: {
            subscriptionId,
            parkerId:
              parkerId === 0 || parkerId == null ? parker.parker.parkerId : parkerId,
            facilityId,
            integrationPartyId,
            vehicleId
          },
        });
        updatedIntegration = response.data.parkerIntegrationParty;
      }

      return updatedIntegration;
    } catch (err) {
      toast.error("Failed to update vehicle integration status");
      throw err;
    }
  };

  const handleToggleIntegration = async (
    integrationPartyId: number,
    subscriptionId: number,
    parkerId: number,
    vehicleId: number | undefined,
    facilityId: number,
    newStatus: IntegrationStatus
  ) => {
    const updatedIntegration = vehicleId
      ? await handleVehicleIntegration(integrationPartyId, subscriptionId, parkerId, vehicleId, facilityId, newStatus)
      : await handleSubscriptionIntegration(integrationPartyId, subscriptionId, parkerId, facilityId, newStatus);

      if (!updatedIntegration)
      {
        toast.error("Failed to update integration status");
        return;
      }

      toast.success(
        `Integration successfully ${newStatus === IntegrationStatus.Activated ? "activated" : "deactivated"}`
      );

    setParkerIntegrations((prev) => {
      const exists = prev.some(
        (item) =>
          item.integrationPartyId === updatedIntegration.integrationPartyId &&
          item.parkerId === updatedIntegration.parkerId &&
          item.vehicleId === updatedIntegration.vehicleId
      );

      if (exists) {
        return prev.map((item) =>
          item.integrationPartyId === updatedIntegration.integrationPartyId &&
          item.parkerId === updatedIntegration.parkerId &&
          item.vehicleId === updatedIntegration.vehicleId
            ? { ...item, ...updatedIntegration }
            : item
        );
      } else {
        return [...prev, updatedIntegration];
      }
    });

    if (setMainParkerIntegrations) {
      setMainParkerIntegrations((prev) =>
        prev.map((item) =>
          item.integrationPartyId === integrationPartyId && item.parkerId === updatedIntegration.parkerId
            ? { ...item, ...updatedIntegration }
            : item
        )
      );
    }
  };

  const openChooseModalForParkersList = async () => {
    const result = await Api.vehicleListByParker(
      parker.parker.parkerId,
      facilityId
    );

    if (result.isOk) {
      setChooseModalVehicles(result.value);
    } else {
      setChooseModalVehicles([]);
    }
    setChooseModalExclude(parker!.vehicles.map((x) => x.vehicleId));
    setShowChoose(true);
  };

  async function vehicleRemove(vehicle: Vehicle) {
    const deleteRes = await Api.subscriptionVehicleDelete(
      facilityId,
      selectedParkerSub.subscriptionId,
      vehicle.vehicleId,
      {
        vehicleId: vehicle.vehicleId,
      }
    );

    if (!deleteRes.isOk) {
      toast.error(deleteRes.error as string);
      return;
    }

    setParkers((prevParkers) =>
      prevParkers.map((p) => {
        if (p.parker.parkerId === parker.parker.parkerId) {
          const updatedVehicles = p.vehicles?.filter(
            (v: any) => v.vehicleId !== vehicle.vehicleId
          );

          return {
            ...p,
            vehicles: updatedVehicles,
            lpn:
              updatedVehicles.length > 0
                ? updatedVehicles
                    .map((v: any) => v.licensePlateNumber)
                    .join(" | ")
                : "N/A",
            parker: {
              ...p.parker,
              vehicles: updatedVehicles,
            },
          };
        }

        return p;
      })
    );

    setParker((prev) =>
      prev
        ? {
            ...prev,
            vehicles: prev.vehicles?.filter(
              (v) => v.vehicleId !== vehicle.vehicleId
            ),
            parker: {
              ...prev.parker,
              vehicles: prev.parker.vehicles?.filter(
                (v) => v.vehicleId !== vehicle.vehicleId
              ),
            },
          }
        : prev
    );

    toast.success("Vehicle Removed Successfully");
  }

  async function vehicleAdd(data: Vehicle) {
    const addRes = await Api.vehicleAdd(
      facilityId,
      parker.parker.parkerId,
      data
    );

    if (!addRes.isOk) {
      toast.error(addRes.error);
      return;
    }

    await vehicleLink(addRes.value);
  }

  async function vehicleLink(vehicle: Vehicle) {
    const addToSub = await Api.subscriptionVehicleAdd(
      facilityId,
      selectedParkerSub.subscriptionId,
      { vehicleId: vehicle.vehicleId }
    );

    if (!addToSub.isOk) {
      toast.error(addToSub.error);
      return;
    }

    setParkers((prevParkers) =>
      prevParkers.map((p) =>
        p.parker.parkerId === parker.parker.parkerId
          ? {
              ...p,
              vehicles: [...p.vehicles, vehicle],
              parker: {
                ...p.parker,
                vehicles: [...(p.parker?.vehicles ?? []), vehicle],
              },
            }
          : p
      )
    );

    const updatedVehicles = [...parker.vehicles, vehicle];
    setParker({
      ...parker,
      vehicles: updatedVehicles,
      parker: {
        ...parker.parker,
        vehicles: updatedVehicles,
      },
    });

    toast.success("Vehicle Added Successfully");
  }

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Parker</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {hasMoreVehiclesThanSpots && (
          <div
            className="alert alert-purple my-2 d-flex align-items-center gap-2"
            style={{ backgroundColor: "var(--color-light)" }}
          >
            <InfoCircleFill />
            <div>
              There are more vehicles than spaces available. Only{" "}
              <strong>{allowedSpots}</strong> vehicle
              {allowedSpots > 1 ? "s" : ""} are permitted to be parked at a
              time.
            </div>
          </div>
        )}
        <Card className="mb-4">
          <Card.Header>
            <div>Settings</div>
          </Card.Header>
          <div className="row g-3 p-4">
            {!parker.parker.isManaged && (
              <div className="col-md-6">
                <Input
                  icon={<Person />}
                  type="text"
                  label="Name"
                  disabled={selectedParkerSub.status != SubscriptionStatus.Active}
                  inputClassName="text-start"
                  {...register("parker.name", {
                    required: "Name is required",
                  })}
                  error={errors.parker?.name}
                />
              </div>
            )}

            <div className="col-md-6">
              <Input
                icon={<Hash />}
                type="number"
                label="Quantity"
                disabled={selectedParkerSub.status != SubscriptionStatus.Active}
                inputClassName="text-end"
                min={1}
                {...register("quantity", {
                  required: "Quantity is required",
                  min: {
                    value: 1,
                    message: "Quantity must be at least 1",
                  },
                  valueAsNumber: true,
                })}
                error={errors.quantity}
              />
            </div>

            <div className="d-flex justify-content-end gap-2 mt-3 mb-3">
              <Button
                className={`w-100 rounded-pill ${
                  !isDirty
                    ? "bg-secondary-subtle text-dark border-0 opacity-100"
                    : "btn-main"
                }`}
                type="submit"
                onClick={handleSubmit(onSubmit)}
                disabled={!isDirty}
              >
                Save
              </Button>
            </div>
          </div>
        </Card>

          {selectedParkerSub.facilityHasIntegrationsEnabled && isAdminRole(auth.account?.roles ?? []) && (
            <ParkerIntegrations
              integrations={subscriptionIntegrations}
              subscriptionId={selectedParkerSub.subscriptionId}
              facilityId={facilityId}
              setPendingToggle={setPendingToggle}
              setShowToggleConfirm={setShowToggleConfirm}
              setQrCodeData={setIntegrationQrCodeData}
              setShowQrModal={setShowIntegrationQrModal}
            />
          )}

        <Card className="mt-1">
          <Card.Header
            style={{
              display: "grid",
              gridTemplateColumns: "max-content 1fr max-content max-content",
            }}
          >
            <div>Vehicles</div>
            <div></div>
            <div>
              {selectedParkerSub.status === SubscriptionStatus.Active && (
                <>
                  <Link
                    to=""
                    onClick={(e) => {
                      e.preventDefault();
                      setShowAdd(true);
                    }}
                  >
                    Add New
                  </Link>
                  <>
                    &nbsp;|&nbsp;
                    <Link
                      to=""
                      onClick={(e) => {
                        e.preventDefault();
                        openChooseModalForParkersList();
                      }}
                    >
                      Add Existing
                    </Link>
                  </>
                </>
              )}
            </div>
          </Card.Header>
          <Card.Body>
            {parker.vehicles?.length === 0 ? (
              <div className="text-secondary">
                This parker hasn’t added any vehicles yet.
              </div>
            ) : (
              parker.vehicles.map((v) => (
                <ReservationVehicleCard
                  key={v.vehicleId}
                  vehicle={v}
                  subscriptionStatus={selectedParkerSub.status}
                  facilityId={facilityId}
                  subscriptionId={selectedParkerSub.subscriptionId}
                  onRemove={() => 
                    {
                      setVehicleToDelete(v);
                      setShowParkerVehicleRemoveConfirm(true);
                    }
                  }
                  integrations={vehicleIntegrations}
                  setPendingToggle={setPendingToggle}
                  setShowToggleConfirm={setShowToggleConfirm}
                />
              ))
            )}
          </Card.Body>
        </Card>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="button"
          className="w-100"
          onClick={() => {
            onClose();
            reset();
          }}
        >
          Close
        </Button>
      </Modal.Footer>

      <AddVehicleModal
        show={showAdd}
        setShow={setShowAdd}
        onSubmit={vehicleAdd}
      />

      <ChooseVehicleModal
        show={showChoose}
        setShow={setShowChoose}
        onSubmit={vehicleLink}
        vehicles={chooseModalVehicles}
        exclude={chooseModalExclude}
        onClose={() => setShowChoose(false)}
      />

      <QrCodeModal
        show={showIntegrationQrModal}
        onHide={() => setShowIntegrationQrModal(false)}
        value={integrationQrCodeData}
        height={256}
      />
      
      <ConfirmModal
        title={`${pendingToggle?.newStatus === IntegrationStatus.Activated ? "Activate" : "Deactivate"} Integration`}
        message={`Are you sure you want to ${pendingToggle?.newStatus === IntegrationStatus.Activated ? "activate" : "deactivate"} this integration?`}
        show={showToggleConfirm}
        setShow={setShowToggleConfirm}
        onConfirm={() => {
          if (pendingToggle) {
            handleToggleIntegration(
              pendingToggle.integrationPartyId,
              pendingToggle.subscriptionId,
              pendingToggle.parkerId,
              pendingToggle.vehicleId,
              pendingToggle.facilityId,
              pendingToggle.newStatus,
            );
          }
          setShowToggleConfirm(false);
          setPendingToggle(null);
        }}
      />

      <ConfirmModal
        title={`Remove Vehicle`}
        message={`Are you sure you want to remove this vehicle from the parker?`}
        show={showParkerVehicleRemoveConfirm}
        setShow={setShowParkerVehicleRemoveConfirm}
        onConfirm={() => {
          if (vehicleToDelete)
          {
            vehicleRemove(vehicleToDelete)
          }
          setVehicleToDelete(null)
          setShowParkerVehicleRemoveConfirm(false);
        }}
      />
    </Modal>
  );
};

export default ParkerEditModal;
